import React, { useState, useEffect } from 'react';
import { branding } from '../constants/branding';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { fetchProfile, updateProfile, changePassword, uploadAvatar } from '../store/slices/profileSlice';
import TwoFactorAuthManager from '../components/TwoFactorAuth/TwoFactorAuthManager';

// Account activity data
const accountActivity = [
  {
    id: 1,
    type: 'Login',
    description: 'Successful login from New York',
    timestamp: '2024-06-01 14:30',
    device: 'iPhone 14',
    location: 'New York, NY',
    status: 'Success'
  },
  {
    id: 2,
    type: 'Payment',
    description: 'Payment to Amazon.com',
    timestamp: '2024-05-31 16:45',
    device: 'Desktop',
    location: 'New York, NY',
    status: 'Completed'
  },
  {
    id: 3,
    type: 'Transfer',
    description: 'Transfer to Emily Christofides',
    timestamp: '2024-05-30 09:15',
    device: 'iPhone 14',
    location: 'New York, NY',
    status: 'Completed'
  },
  {
    id: 4,
    type: 'Login',
    description: 'Failed login attempt',
    timestamp: '2024-05-29 22:10',
    device: 'Unknown',
    location: 'Unknown',
    status: 'Failed'
  }
];

// Security settings
const securitySettings = [
  {
    id: 'twoFactor',
    title: 'Two-Factor Authentication',
    description: 'Add an extra layer of security to your account',
    enabled: true,
    icon: '🔐'
  },
  {
    id: 'biometric',
    title: 'Biometric Login',
    description: 'Use fingerprint or face recognition',
    enabled: true,
    icon: '👆'
  },
  {
    id: 'loginAlerts',
    title: 'Login Alerts',
    description: 'Get notified of new login attempts',
    enabled: true,
    icon: '🔔'
  },
  {
    id: 'transactionAlerts',
    title: 'Transaction Alerts',
    description: 'Real-time notifications for transactions',
    enabled: false,
    icon: '💰'
  }
];

const Profile: React.FC = () => {
  const dispatch = useDispatch();
  const { profile, isLoading, error } = useSelector((state: RootState) => state.profile);
  const token = useSelector((state: RootState) => state.auth.token);

  const [activeTab, setActiveTab] = useState<'personal' | 'security' | 'preferences' | 'activity'>('personal');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [editForm, setEditForm] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [kycFiles, setKycFiles] = useState<{ utilityBill?: File | null; passport?: File | null; idFront?: File | null; idBack?: File | null }>({});
  const [kybFiles, setKybFiles] = useState<{ memorandum?: File | null; articles?: File | null; businessUtilityBill?: File | null }>({});
  const [complianceUploading, setComplianceUploading] = useState(false);
  const [complianceError, setComplianceError] = useState<string | null>(null);
  const [complianceSuccess, setComplianceSuccess] = useState<string | null>(null);


  useEffect(() => {
    // Only fetch profile if it doesn't exist and isn't already loading
    if (!profile && !isLoading) {
      dispatch(fetchProfile() as any);
    }
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch, profile, isLoading]);

  useEffect(() => {
    if (profile) {
      const address = (profile.address || {}) as {
        street?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        country?: string;
      };
      setEditForm({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        phone: profile.phone || '',
        street: address.street || '',
        city: address.city || '',
        state: address.state || '',
        zipCode: address.zipCode || ''
      });
    }
  }, [profile]);

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    if (editForm) {
      dispatch(updateProfile({
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        phone: editForm.phone,
        address: {
          street: editForm.street,
          city: editForm.city,
          state: editForm.state,
          zipCode: editForm.zipCode,
          country: profile?.address.country || ''
        }
      }) as any).then(() => setShowEditModal(false));
    }
  };

  const [passwordChangeState, setPasswordChangeState] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
    loading: false,
    error: null as string | null,
    success: false
  });

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!passwordChangeState.oldPassword || !passwordChangeState.newPassword || !passwordChangeState.confirmPassword) {
      setPasswordChangeState(prev => ({ ...prev, error: 'All fields are required' }));
      return;
    }

    if (passwordChangeState.newPassword.length < 8) {
      setPasswordChangeState(prev => ({ ...prev, error: 'New password must be at least 8 characters long' }));
      return;
    }

    if (passwordChangeState.newPassword !== passwordChangeState.confirmPassword) {
      setPasswordChangeState(prev => ({ ...prev, error: 'New password and confirm password do not match' }));
      return;
    }

    if (passwordChangeState.oldPassword === passwordChangeState.newPassword) {
      setPasswordChangeState(prev => ({ ...prev, error: 'New password must be different from the current password' }));
      return;
    }

    setPasswordChangeState(prev => ({ ...prev, loading: true, error: null, success: false }));
    
    try {
      await dispatch(changePassword({ 
        oldPassword: passwordChangeState.oldPassword, 
        newPassword: passwordChangeState.newPassword 
      }) as any).unwrap();
      
      setPasswordChangeState(prev => ({ ...prev, loading: false, success: true }));
      
      // Reset form and close modal after 2 seconds
      setTimeout(() => {
        setPasswordChangeState({
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
          loading: false,
          error: null,
          success: false
        });
        setShowPasswordModal(false);
      }, 2000);
    } catch (err: any) {
      setPasswordChangeState(prev => ({ 
        ...prev, 
        loading: false, 
        error: err.message || err || 'Failed to change password. Please check your current password.' 
      }));
    }
  };

  const toggleSecuritySetting = (id: string) => {
    // Here you would handle toggling security settings
    console.log(`Toggling ${id}`);
  };

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: '👤' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'preferences', label: 'Preferences', icon: '⚙️' },
    { id: 'activity', label: 'Activity', icon: '📊' }
  ] as const;

  const address = (profile?.address || {}) as {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  const preferences = profile?.preferences || {};
  const notifications = preferences.notifications || {};

  // --- SEPA LIMITS/USAGE LOGIC ---
  const sepaLimits = profile ? {
    daily: Number(profile.sepaDailyLimit ?? 0),
    monthly: Number(profile.sepaMonthlyLimit ?? 0),
    yearly: Number(profile.sepaYearlyLimit ?? 0),
    single: Number(profile.sepaSingleTransactionLimit ?? 0),
    currency: profile.sepaLimitCurrency ?? 'EUR',
  } : { daily: 0, monthly: 0, yearly: 0, single: 0, currency: 'EUR' };
  const sepaUsage = profile ? {
    daily: Number(profile.sepaDailyUsage ?? 0),
    monthly: Number(profile.sepaMonthlyUsage ?? 0),
    yearly: Number(profile.sepaYearlyUsage ?? 0),
  } : { daily: 0, monthly: 0, yearly: 0 };
  const over80 = (limit: number, usage: number) => limit > 0 && usage / limit >= 0.8;
  const show80Daily = over80(sepaLimits.daily, sepaUsage.daily);
  const show80Monthly = over80(sepaLimits.monthly, sepaUsage.monthly);
  const show80Yearly = over80(sepaLimits.yearly, sepaUsage.yearly);

  // Avatar upload handler
  const handleAvatarClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Use the uploadAvatar action from profileSlice which uses getApiBaseUrl()
      const result = await dispatch(uploadAvatar(file) as any);
      if (uploadAvatar.fulfilled.match(result)) {
        // Refresh profile to show new avatar
        dispatch(fetchProfile() as any);
      } else {
        alert(result.payload || 'Failed to upload avatar.');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to upload avatar.');
    } finally {
      setUploading(false);
    }
  };

  // Compliance upload handler
  const handleComplianceFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: string, isBusiness: boolean) => {
    const file = e.target.files?.[0] || null;
    if (isBusiness) {
      setKybFiles(prev => ({ ...prev, [field]: file }));
    } else {
      setKycFiles(prev => ({ ...prev, [field]: file }));
    }
  };

  const handleComplianceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setComplianceError(null);
    setComplianceSuccess(null);
    setComplianceUploading(true);
    try {
      // Only 'PARENT' and 'CHILD' are valid accountType values. Use 'PARENT' to represent business/parent accounts.
      const isBusiness = profile?.accountType === 'PARENT';
      const formData = new FormData();
      if (isBusiness) {
        if (kybFiles.memorandum) formData.append('memorandum', kybFiles.memorandum);
        if (kybFiles.articles) formData.append('articles', kybFiles.articles);
        if (kybFiles.businessUtilityBill) formData.append('businessUtilityBill', kybFiles.businessUtilityBill);
      } else {
        if (kycFiles.utilityBill) formData.append('utilityBill', kycFiles.utilityBill);
        if (kycFiles.passport) formData.append('passport', kycFiles.passport);
        if (kycFiles.idFront) formData.append('idFront', kycFiles.idFront);
        if (kycFiles.idBack) formData.append('idBack', kycFiles.idBack);
      }
      const endpoint = isBusiness ? '/compliance/kyb' : '/compliance/kyc';
      const response = await fetch(`http://localhost:8080${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      if (!response.ok) throw new Error('Failed to upload compliance documents');
      setComplianceSuccess('Documents uploaded successfully!');
      setShowComplianceModal(false);
      setKycFiles({});
      setKybFiles({});
    } catch (err: any) {
      setComplianceError(err.message || 'Failed to upload documents');
    } finally {
      setComplianceUploading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      // background: branding.backgroundGradient,
      fontFamily: branding.fontFamily,
      padding: isMobile ? '24px 16px' : 0,
      margin: 0,
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <div style={{
        maxWidth: 800,
        margin: isMobile ? '0 auto' : '32px auto',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          marginBottom: 32,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '16px' : 0
        }}>
          <div>
            <h1 style={{
              color: branding.textColor,
              fontWeight: 700,
              fontSize: isMobile ? 24 : 28,
              margin: 0,
              marginBottom: 8,
            }}>Profile</h1>
            <p style={{
              color: '#666',
              fontSize: isMobile ? 14 : 16,
              margin: 0,
            }}>Manage your account settings and preferences</p>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => setShowEditModal(true)}
              style={{
                padding: '12px 24px',
                background: branding.primaryColor,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: isMobile ? 14 : 16,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 4px 16px rgba(10,132,255,0.2)',
                width: isMobile ? '100%' : 'auto',
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}
            >
              <span>✏️</span> Edit Profile
            </button>
            <button
              onClick={() => setShowComplianceModal(true)}
              style={{
                padding: '12px 24px',
                background: branding.warningColor || '#f59e42',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: isMobile ? 14 : 16,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 4px 16px rgba(255,193,7,0.15)',
                width: isMobile ? '100%' : 'auto',
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}
            >
              <span role="img" aria-label="compliance">📝</span> Compliance
            </button>
          </div>
        </div>
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: isMobile ? 20 : 32,
          marginBottom: 32,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr auto',
            gap: isMobile ? 16 : 24,
            alignItems: 'center',
          }}>
            {/* Avatar with upload icon */}
            <div style={{ position: 'relative', width: isMobile ? 80 : 100, height: isMobile ? 80 : 100, margin: isMobile ? '0 auto' : 0 }}>
              <img
                src={
                  profile?.avatarBase64
                    ? `data:image/jpeg;base64,${profile.avatarBase64}`
                    : undefined
                }
                alt={`${profile?.firstName} ${profile?.lastName}`}
                style={{
                  width: isMobile ? 80 : 100,
                  height: isMobile ? 80 : 100,
                  borderRadius: '50%',
                  border: '4px solid ' + branding.primaryColor,
                  boxShadow: '0 4px 16px rgba(10,132,255,0.2)',
                  objectFit: 'cover',
                  filter: uploading ? 'grayscale(0.7)' : undefined,
                  opacity: uploading ? 0.7 : 1
                }}
                onClick={handleAvatarClick}
              />
              {/* Upload icon overlay */}
              <div
                onClick={handleAvatarClick}
                style={{
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  background: '#fff',
                  borderRadius: '50%',
                  border: '2px solid ' + branding.primaryColor,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(10,132,255,0.12)',
                  cursor: 'pointer',
                  zIndex: 2
                }}
                title="Upload new profile image"
              >
                <span role="img" aria-label="Upload" style={{ fontSize: 18 }}>📷</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarChange}
                disabled={uploading}
              />
              {uploading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'rgba(255,255,255,0.5)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  color: branding.primaryColor,
                  zIndex: 3
                }}>
                  Uploading...
                </div>
              )}
            </div>

            {/* User Info */}
            <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
              <h2 style={{
                margin: 0,
                marginBottom: 8,
                color: branding.textColor,
                fontSize: isMobile ? 20 : 24,
                fontWeight: 700,
              }}>{profile?.firstName} {profile?.lastName}</h2>
              <p style={{
                margin: 0,
                marginBottom: 4,
                color: '#666',
                fontSize: isMobile ? 14 : 16,
              }}>{profile?.email}</p>
              <p style={{
                margin: 0,
                color: '#888',
                fontSize: isMobile ? 12 : 14,
              }}>
                {profile?.memberSince ? `Member since ${new Date(profile.memberSince).toLocaleDateString()}` : ''}
              </p>
            </div>

            {/* Quick Stats */}
            <div style={{
              textAlign: isMobile ? 'center' : 'right',
              marginTop: isMobile ? 16 : 0
            }}>
              <div style={{
                fontSize: isMobile ? 12 : 14,
                color: '#666',
                marginBottom: 4,
              }}>Account Number</div>
              <div style={{
                fontSize: isMobile ? 16 : 18,
                fontWeight: 700,
                color: branding.primaryColor,
                marginBottom: 8,
              }}>{profile?.accountNumber}</div>
              <div style={{
                fontSize: isMobile ? 12 : 14,
                color: '#666',
                marginBottom: 4,
              }}>Security Level</div>
              <div style={{
                fontSize: isMobile ? 14 : 16,
                fontWeight: 600,
                color: branding.successColor,
              }}>{profile?.securityLevel}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: isMobile ? '16px 12px' : 32,
          marginBottom: 32,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          width: '100%',
          boxSizing: 'border-box',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}>
          <div style={{
            display: 'flex',
            gap: isMobile ? 2 : 8,
            marginBottom: 32,
            borderBottom: '1px solid #eee',
            minWidth: isMobile ? 'max-content' : 'auto',
            paddingBottom: isMobile ? 1 : 0,
          }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: isMobile ? '8px 12px' : '12px 24px',
                  background: activeTab === tab.id ? branding.primaryColor : 'transparent',
                  color: activeTab === tab.id ? '#fff' : branding.textColor,
                  border: 'none',
                  borderRadius: '12px 12px 0 0',
                  fontSize: isMobile ? 13 : 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                  minWidth: isMobile ? 'auto' : '120px',
                  justifyContent: 'center'
                }}
              >
                <span style={{ fontSize: isMobile ? 14 : 16 }}>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'personal' && (
            <div>
              {/* SEPA Limits & Usage Panel */}
              {profile && sepaLimits.single > 0 && (
                <div style={{
                  background: '#f7fafd',
                  border: '1.5px solid #eee',
                  borderRadius: 14,
                  padding: 18,
                  marginBottom: 24,
                  marginTop: 4,
                  boxShadow: '0 2px 8px rgba(10,132,255,0.04)',
                  maxWidth: 480,
                }}>
                  <div style={{ fontWeight: 700, color: branding.primaryColor, marginBottom: 8 }}>SEPA Transaction Limits</div>
                  <div style={{ fontSize: 14, color: '#444', marginBottom: 4 }}>Single: {sepaLimits.currency} {sepaLimits.single.toLocaleString()} | Daily: {sepaLimits.currency} {sepaLimits.daily.toLocaleString()} | Monthly: {sepaLimits.currency} {sepaLimits.monthly.toLocaleString()} | Yearly: {sepaLimits.currency} {sepaLimits.yearly.toLocaleString()}</div>
                  <div style={{ fontSize: 13, color: '#666' }}>
                    Usage: Daily: {sepaLimits.currency} {sepaUsage.daily.toLocaleString()} | Monthly: {sepaLimits.currency} {sepaUsage.monthly.toLocaleString()} | Yearly: {sepaLimits.currency} {sepaUsage.yearly.toLocaleString()}
                  </div>
                  {(show80Daily || show80Monthly || show80Yearly) && (
                    <div style={{ color: branding.warningColor || '#eab308', fontWeight: 600, marginTop: 8 }}>
                      {show80Daily && `Warning: You are above 80% of your daily SEPA limit. `}
                      {show80Monthly && `Warning: You are above 80% of your monthly SEPA limit. `}
                      {show80Yearly && `Warning: You are above 80% of your yearly SEPA limit.`}
                    </div>
                  )}
                </div>
              )}
              <h3 style={{
                margin: 0,
                marginBottom: isMobile ? 16 : 24,
                color: branding.textColor,
                fontSize: isMobile ? 18 : 20,
                fontWeight: 600,
              }}>Personal Information</h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? 20 : 24,
              }}>
                <div>
                  <div style={{ 
                    fontSize: isMobile ? 13 : 14, 
                    color: '#666', 
                    marginBottom: isMobile ? 4 : 8,
                    fontWeight: 500 
                  }}>Full Name</div>
                  <div style={{ 
                    fontSize: isMobile ? 15 : 16, 
                    fontWeight: 600, 
                    color: branding.textColor,
                    background: '#f7fafd',
                    padding: isMobile ? '12px 16px' : '16px',
                    borderRadius: 12,
                    border: '1px solid #eee'
                  }}>
                    {profile?.firstName} {profile?.lastName}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: isMobile ? 13 : 14, 
                    color: '#666', 
                    marginBottom: isMobile ? 4 : 8,
                    fontWeight: 500 
                  }}>Email Address</div>
                  <div style={{ 
                    fontSize: isMobile ? 15 : 16, 
                    fontWeight: 600, 
                    color: branding.textColor,
                    background: '#f7fafd',
                    padding: isMobile ? '12px 16px' : '16px',
                    borderRadius: 12,
                    border: '1px solid #eee'
                  }}>
                    {profile?.email}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: isMobile ? 13 : 14, 
                    color: '#666', 
                    marginBottom: isMobile ? 4 : 8,
                    fontWeight: 500 
                  }}>Phone Number</div>
                  <div style={{ 
                    fontSize: isMobile ? 15 : 16, 
                    fontWeight: 600, 
                    color: branding.textColor,
                    background: '#f7fafd',
                    padding: isMobile ? '12px 16px' : '16px',
                    borderRadius: 12,
                    border: '1px solid #eee'
                  }}>
                    {profile?.phone}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: isMobile ? 13 : 14, 
                    color: '#666', 
                    marginBottom: isMobile ? 4 : 8,
                    fontWeight: 500 
                  }}>Date of Birth</div>
                  <div style={{ 
                    fontSize: isMobile ? 15 : 16, 
                    fontWeight: 600, 
                    color: branding.textColor,
                    background: '#f7fafd',
                    padding: isMobile ? '12px 16px' : '16px',
                    borderRadius: 12,
                    border: '1px solid #eee'
                  }}>
                    {profile?.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString() : ''}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: isMobile ? 13 : 14, 
                    color: '#666', 
                    marginBottom: isMobile ? 4 : 8,
                    fontWeight: 500 
                  }}>Address</div>
                  <div style={{ 
                    fontSize: isMobile ? 15 : 16, 
                    fontWeight: 600, 
                    color: branding.textColor,
                    background: '#f7fafd',
                    padding: isMobile ? '12px 16px' : '16px',
                    borderRadius: 12,
                    border: '1px solid #eee'
                  }}>
                    {address.street || ''}<br />
                    {address.city || ''}, {address.state || ''} {address.zipCode || ''}<br />
                    {address.country || ''}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 24,
              }}>
                <h3 style={{
                  margin: 0,
                  color: branding.textColor,
                  fontSize: isMobile ? 18 : 20,
                  fontWeight: 600,
                }}>Security Settings</h3>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  style={{
                    padding: '8px 16px',
                    background: '#f7fafd',
                    border: '1.5px solid #eee',
                    borderRadius: 8,
                    fontSize: isMobile ? 12 : 14,
                    fontWeight: 600,
                    color: branding.textColor,
                    cursor: 'pointer',
                  }}
                >
                  Change Password
                </button>
              </div>
              <div style={{ display: 'grid', gap: 16 }}>
                {/* Two-Factor Authentication - Special handling with manager component */}
                <div
                  style={{
                    padding: '20px',
                    background: '#f7fafd',
                    borderRadius: 12,
                    border: '1px solid #eee',
                  }}
                >
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                      <span style={{ fontSize: 24 }}>🔐</span>
                      <div>
                        <div style={{ fontWeight: 600, color: branding.textColor, marginBottom: 4 }}>
                          Two-Factor Authentication
                        </div>
                        <div style={{ fontSize: 14, color: '#666' }}>
                          Add an extra layer of security to your account
                        </div>
                      </div>
                    </div>
                    <TwoFactorAuthManager />
                  </div>
                </div>
                
                {/* Other security settings */}
                {securitySettings.filter(setting => setting.id !== 'twoFactor').map(setting => (
                  <div
                    key={setting.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '20px',
                      background: '#f7fafd',
                      borderRadius: 12,
                      border: '1px solid #eee',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <span style={{ fontSize: 24 }}>{setting.icon}</span>
                      <div>
                        <div style={{ fontWeight: 600, color: branding.textColor, marginBottom: 4 }}>
                          {setting.title}
                        </div>
                        <div style={{ fontSize: 14, color: '#666' }}>
                          {setting.description}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        width: 48,
                        height: 24,
                        background: setting.enabled ? branding.primaryColor : '#ccc',
                        borderRadius: 12,
                        position: 'relative',
                        cursor: 'pointer',
                      }}
                      onClick={() => toggleSecuritySetting(setting.id)}
                    >
                      <div style={{
                        width: 20,
                        height: 20,
                        background: '#fff',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: 2,
                        left: setting.enabled ? 'auto' : 2,
                        right: setting.enabled ? 2 : 'auto',
                        transition: 'all 0.2s',
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div>
              <h3 style={{
                margin: 0,
                marginBottom: isMobile ? 16 : 24,
                color: branding.textColor,
                fontSize: isMobile ? 18 : 20,
                fontWeight: 600,
              }}>Account Preferences</h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? 20 : 24,
              }}>
                <div>
                  <div style={{ 
                    fontSize: isMobile ? 13 : 14, 
                    color: '#666', 
                    marginBottom: isMobile ? 4 : 8,
                    fontWeight: 500 
                  }}>Language</div>
                  <div style={{ 
                    fontSize: isMobile ? 15 : 16, 
                    fontWeight: 600, 
                    color: branding.textColor,
                    background: '#f7fafd',
                    padding: isMobile ? '12px 16px' : '16px',
                    borderRadius: 12,
                    border: '1px solid #eee'
                  }}>
                    {preferences.language}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: isMobile ? 13 : 14, 
                    color: '#666', 
                    marginBottom: isMobile ? 4 : 8,
                    fontWeight: 500 
                  }}>Currency</div>
                  <div style={{ 
                    fontSize: isMobile ? 15 : 16, 
                    fontWeight: 600, 
                    color: branding.textColor,
                    background: '#f7fafd',
                    padding: isMobile ? '12px 16px' : '16px',
                    borderRadius: 12,
                    border: '1px solid #eee'
                  }}>
                    {preferences.currency}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: isMobile ? 13 : 14, 
                    color: '#666', 
                    marginBottom: isMobile ? 4 : 8,
                    fontWeight: 500 
                  }}>Timezone</div>
                  <div style={{ 
                    fontSize: isMobile ? 15 : 16, 
                    fontWeight: 600, 
                    color: branding.textColor,
                    background: '#f7fafd',
                    padding: isMobile ? '12px 16px' : '16px',
                    borderRadius: 12,
                    border: '1px solid #eee'
                  }}>
                    {preferences.timezone}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: isMobile ? 13 : 14, 
                    color: '#666', 
                    marginBottom: isMobile ? 12 : 16,
                    fontWeight: 500 
                  }}>Notification Preferences</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 12 }}>
                    {Object.entries(notifications).map(([key, value]) => (
                      <div key={key} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: isMobile ? '10px 12px' : '12px 16px',
                        background: '#f7fafd',
                        borderRadius: 8,
                        border: '1px solid #eee',
                      }}>
                        <span style={{ 
                          fontWeight: 500, 
                          color: branding.textColor, 
                          textTransform: 'capitalize',
                          fontSize: isMobile ? 14 : 15 
                        }}>
                          {key} Notifications
                        </span>
                        <div style={{
                          width: 36,
                          height: 20,
                          background: value ? branding.primaryColor : '#ccc',
                          borderRadius: 10,
                          position: 'relative',
                          cursor: 'pointer',
                        }}>
                          <div style={{
                            width: 16,
                            height: 16,
                            background: '#fff',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: 2,
                            left: value ? 'auto' : 2,
                            right: value ? 2 : 'auto',
                            transition: 'all 0.2s',
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div>
              <h3 style={{
                margin: 0,
                marginBottom: 24,
                color: branding.textColor,
                fontSize: isMobile ? 18 : 20,
                fontWeight: 600,
              }}>Recent Activity</h3>
              <div style={{ display: 'grid', gap: 16 }}>
                {accountActivity.map(activity => (
                  <div
                    key={activity.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '20px',
                      background: '#f7fafd',
                      borderRadius: 12,
                      border: '1px solid #eee',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: activity.status === 'Success' || activity.status === 'Completed' 
                          ? branding.successColor 
                          : branding.errorColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 20,
                      }}>
                        {activity.type === 'Login' ? '🔐' : activity.type === 'Payment' ? '💰' : '📤'}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: branding.textColor, marginBottom: 4 }}>
                          {activity.description}
                        </div>
                        <div style={{ fontSize: 14, color: '#666' }}>
                          {activity.device} • {activity.location}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
                        {new Date(activity.timestamp).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: 14, color: '#666' }}>
                        {new Date(activity.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 24,
            padding: 40,
            maxWidth: 600,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}>
            <h3 style={{
              margin: 0,
              marginBottom: 24,
              color: branding.textColor,
              fontSize: 24,
              fontWeight: 700,
              textAlign: 'center',
            }}>Edit Profile</h3>

            <form onSubmit={handleSaveProfile}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 8,
                    fontWeight: 600,
                    color: branding.textColor,
                  }}>First Name</label>
                  <input
                    type="text"
                    value={editForm?.firstName || ''}
                    onChange={e => setEditForm({...editForm, firstName: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 12,
                      border: '1.5px solid #eee',
                      fontSize: 15,
                      color: branding.textColor,
                      background: '#f7fafd',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 8,
                    fontWeight: 600,
                    color: branding.textColor,
                  }}>Last Name</label>
                  <input
                    type="text"
                    value={editForm?.lastName || ''}
                    onChange={e => setEditForm({...editForm, lastName: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 12,
                      border: '1.5px solid #eee',
                      fontSize: 15,
                      color: branding.textColor,
                      background: '#f7fafd',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 8,
                    fontWeight: 600,
                    color: branding.textColor,
                  }}>Email</label>
                  <input
                    type="email"
                    value={editForm?.email || ''}
                    onChange={e => setEditForm({...editForm, email: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 12,
                      border: '1.5px solid #eee',
                      fontSize: 15,
                      color: branding.textColor,
                      background: '#f7fafd',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 8,
                    fontWeight: 600,
                    color: branding.textColor,
                  }}>Phone</label>
                  <input
                    type="tel"
                    value={editForm?.phone || ''}
                    onChange={e => setEditForm({...editForm, phone: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 12,
                      border: '1.5px solid #eee',
                      fontSize: 15,
                      color: branding.textColor,
                      background: '#f7fafd',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 8,
                    fontWeight: 600,
                    color: branding.textColor,
                  }}>Street Address</label>
                  <input
                    type="text"
                    value={editForm?.street || ''}
                    onChange={e => setEditForm({...editForm, street: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 12,
                      border: '1.5px solid #eee',
                      fontSize: 15,
                      color: branding.textColor,
                      background: '#f7fafd',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 8,
                    fontWeight: 600,
                    color: branding.textColor,
                  }}>City</label>
                  <input
                    type="text"
                    value={editForm?.city || ''}
                    onChange={e => setEditForm({...editForm, city: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 12,
                      border: '1.5px solid #eee',
                      fontSize: 15,
                      color: branding.textColor,
                      background: '#f7fafd',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 8,
                    fontWeight: 600,
                    color: branding.textColor,
                  }}>State</label>
                  <input
                    type="text"
                    value={editForm?.state || ''}
                    onChange={e => setEditForm({...editForm, state: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 12,
                      border: '1.5px solid #eee',
                      fontSize: 15,
                      color: branding.textColor,
                      background: '#f7fafd',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 8,
                    fontWeight: 600,
                    color: branding.textColor,
                  }}>ZIP Code</label>
                  <input
                    type="text"
                    value={editForm?.zipCode || ''}
                    onChange={e => setEditForm({...editForm, zipCode: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 12,
                      border: '1.5px solid #eee',
                      fontSize: 15,
                      color: branding.textColor,
                      background: '#f7fafd',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 16 }}>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  style={{
                    flex: 1,
                    padding: '16px 0',
                    background: '#fff',
                    border: '1.5px solid #eee',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#666',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '16px 0',
                    background: branding.primaryColor,
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 16px rgba(10,132,255,0.2)',
                  }}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 24,
            padding: 40,
            maxWidth: 480,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}>
            <h3 style={{
              margin: 0,
              marginBottom: 24,
              color: branding.textColor,
              fontSize: 24,
              fontWeight: 700,
              textAlign: 'center',
            }}>Change Password</h3>

            <form onSubmit={handlePasswordChange}>
              {passwordChangeState.error && (
                <div style={{
                  marginBottom: 20,
                  padding: '12px 16px',
                  background: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: 12,
                  color: '#c33',
                  fontSize: 14,
                }}>
                  {passwordChangeState.error}
                </div>
              )}

              {passwordChangeState.success && (
                <div style={{
                  marginBottom: 20,
                  padding: '12px 16px',
                  background: '#efe',
                  border: '1px solid #cfc',
                  borderRadius: 12,
                  color: '#3c3',
                  fontSize: 14,
                }}>
                  Password changed successfully!
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 600,
                  color: branding.textColor,
                }}>Current Password</label>
                <input
                  type="password"
                  name="oldPassword"
                  value={passwordChangeState.oldPassword}
                  onChange={(e) => setPasswordChangeState(prev => ({ ...prev, oldPassword: e.target.value, error: null }))}
                  disabled={passwordChangeState.loading || passwordChangeState.success}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 12,
                    border: '1.5px solid #eee',
                    fontSize: 15,
                    color: branding.textColor,
                    background: passwordChangeState.loading || passwordChangeState.success ? '#f5f5f5' : '#f7fafd',
                    boxSizing: 'border-box',
                    cursor: passwordChangeState.loading || passwordChangeState.success ? 'not-allowed' : 'text',
                  }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 600,
                  color: branding.textColor,
                }}>New Password</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordChangeState.newPassword}
                  onChange={(e) => setPasswordChangeState(prev => ({ ...prev, newPassword: e.target.value, error: null }))}
                  disabled={passwordChangeState.loading || passwordChangeState.success}
                  required
                  minLength={8}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 12,
                    border: '1.5px solid #eee',
                    fontSize: 15,
                    color: branding.textColor,
                    background: passwordChangeState.loading || passwordChangeState.success ? '#f5f5f5' : '#f7fafd',
                    boxSizing: 'border-box',
                    cursor: passwordChangeState.loading || passwordChangeState.success ? 'not-allowed' : 'text',
                  }}
                />
                <small style={{ color: '#666', fontSize: 12, marginTop: 4, display: 'block' }}>
                  Must be at least 8 characters long
                </small>
              </div>
              <div style={{ marginBottom: 32 }}>
                <label style={{
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 600,
                  color: branding.textColor,
                }}>Confirm New Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordChangeState.confirmPassword}
                  onChange={(e) => setPasswordChangeState(prev => ({ ...prev, confirmPassword: e.target.value, error: null }))}
                  disabled={passwordChangeState.loading || passwordChangeState.success}
                  required
                  minLength={8}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 12,
                    border: '1.5px solid #eee',
                    fontSize: 15,
                    color: branding.textColor,
                    background: passwordChangeState.loading || passwordChangeState.success ? '#f5f5f5' : '#f7fafd',
                    boxSizing: 'border-box',
                    cursor: passwordChangeState.loading || passwordChangeState.success ? 'not-allowed' : 'text',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 16 }}>
                <button
                  type="button"
                  onClick={() => {
                    setPasswordChangeState({
                      oldPassword: '',
                      newPassword: '',
                      confirmPassword: '',
                      loading: false,
                      error: null,
                      success: false
                    });
                    setShowPasswordModal(false);
                  }}
                  disabled={passwordChangeState.loading}
                  style={{
                    flex: 1,
                    padding: '16px 0',
                    background: '#fff',
                    border: '1.5px solid #eee',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#666',
                    cursor: passwordChangeState.loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: passwordChangeState.loading ? 0.6 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passwordChangeState.loading || passwordChangeState.success}
                  style={{
                    flex: 1,
                    padding: '16px 0',
                    background: passwordChangeState.loading || passwordChangeState.success ? '#ccc' : branding.primaryColor,
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#fff',
                    cursor: passwordChangeState.loading || passwordChangeState.success ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: passwordChangeState.loading || passwordChangeState.success ? 'none' : '0 4px 16px rgba(10,132,255,0.2)',
                  }}
                >
                  {passwordChangeState.loading ? 'Changing...' : passwordChangeState.success ? 'Success!' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showComplianceModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 24,
            padding: 40,
            maxWidth: 600,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}>
            <h3 style={{
              margin: 0,
              marginBottom: 24,
              color: branding.textColor,
              fontSize: 24,
              fontWeight: 700,
              textAlign: 'center',
            }}>Compliance</h3>
            <form onSubmit={handleComplianceSubmit}>
              {profile?.accountType === 'PARENT' ? (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontWeight: 600 }}>Memorandum of Association or Articles of Association</label>
                    <input type="file" accept="application/pdf,image/*" onChange={e => handleComplianceFileChange(e, 'memorandum', true)} required={!kybFiles.articles} />
                    <span style={{ margin: '0 8px' }}>or</span>
                    <input type="file" accept="application/pdf,image/*" onChange={e => handleComplianceFileChange(e, 'articles', true)} required={!kybFiles.memorandum} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontWeight: 600 }}>Utility Bill (Business Address)</label>
                    <input type="file" accept="application/pdf,image/*" onChange={e => handleComplianceFileChange(e, 'businessUtilityBill', true)} required />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontWeight: 600 }}>Utility Bill</label>
                    <input type="file" accept="application/pdf,image/*" onChange={e => handleComplianceFileChange(e, 'utilityBill', false)} required />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontWeight: 600 }}>Passport Scan</label>
                    <input type="file" accept="application/pdf,image/*" onChange={e => handleComplianceFileChange(e, 'passport', false)} required={!kycFiles.idFront || !kycFiles.idBack} />
                    <span style={{ margin: '0 8px' }}>or</span>
                    <label style={{ fontWeight: 600 }}>ID Scan (Front)</label>
                    <input type="file" accept="application/pdf,image/*" onChange={e => handleComplianceFileChange(e, 'idFront', false)} required={!kycFiles.passport} />
                    <label style={{ fontWeight: 600, marginLeft: 8 }}>ID Scan (Back)</label>
                    <input type="file" accept="application/pdf,image/*" onChange={e => handleComplianceFileChange(e, 'idBack', false)} required={!kycFiles.passport} />
                  </div>
                </>
              )}
              {complianceError && <div style={{ color: 'red', marginBottom: 12 }}>{complianceError}</div>}
              {complianceSuccess && <div style={{ color: 'green', marginBottom: 12 }}>{complianceSuccess}</div>}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 24 }}>
                <button type="button" onClick={() => setShowComplianceModal(false)} style={{ padding: '12px 24px', borderRadius: 12, border: 'none', background: '#eee', color: '#333', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={complianceUploading} style={{ padding: '12px 24px', borderRadius: 12, border: 'none', background: branding.primaryColor, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>{complianceUploading ? 'Uploading...' : 'Submit'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile; 